import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { CalendarDays, CheckCircle2, Circle, CircleDot, Plus, Trash2, User } from 'lucide-react'
import { useMemo, useState } from 'react'
import { localTodayISO } from '../../../../packages/shared/src/saldo'
import { useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { api, type Task } from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tasks',
  component: TasksPage,
})

const PRIORITY_LABEL: Record<Task['priority'], string> = {
  low: 'Niedrig',
  normal: 'Normal',
  high: 'Hoch',
  critical: 'Kritisch',
}
const PRIORITY_COLOR: Record<Task['priority'], string> = {
  low: 'text-text-tertiary',
  normal: 'text-text-secondary',
  high: 'text-warning',
  critical: 'text-danger',
}

type FilterMode = 'assigned' | 'created' | 'all'

/** Fälligkeits-Gruppen: Überfällig / Heute / Diese Woche / Später / Ohne Termin. */
function dueGroup(t: Task, todayISO: string, weekEndISO: string): string {
  if (t.status === 'done') return 'Erledigt'
  if (!t.dueDate) return 'Ohne Termin'
  if (t.dueDate < todayISO) return 'Überfällig'
  if (t.dueDate === todayISO) return 'Heute'
  if (t.dueDate <= weekEndISO) return 'Diese Woche'
  return 'Später'
}
const GROUP_ORDER = ['Überfällig', 'Heute', 'Diese Woche', 'Später', 'Ohne Termin', 'Erledigt']

function TasksPage() {
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<FilterMode>('assigned')
  const [showCreate, setShowCreate] = useState(false)
  const [showDone, setShowDone] = useState(false)

  useMobilePageTitle('Aufgaben')

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data, isLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.listTasks(),
    enabled: !!meData?.user,
  })
  const { data: usersData } = useQuery({
    queryKey: ['users', 'manageable'],
    queryFn: () => api.listManageableUsers(),
    enabled: !!meData?.user,
  })
  // Projekt-Zuordnung (Hub Paket 2): Pool für Selektor + Badges. Gleicher
  // Query-Key wie die Projektseite → geteilter Cache.
  const { data: projectsData } = useQuery({
    queryKey: ['projects', { archived: false }],
    queryFn: () => api.listProjects({ archived: false }),
    enabled: !!meData?.user,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tasks'] })

  const toggle = useMutation({
    mutationFn: (t: Task) =>
      api.updateTask(t.id, { status: t.status === 'done' ? 'open' : 'done' }),
    onSuccess: invalidate,
    onError: (e: Error) => notify.error(e.message),
  })
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteTask(id),
    onSuccess: () => {
      invalidate()
      notify.success('Aufgabe gelöscht')
    },
    onError: (e: Error) => notify.error(e.message),
  })

  const me = meData?.user
  const tasks = data?.tasks ?? []
  const manageableUsers = usersData?.users ?? []
  const projects = projectsData?.projects ?? []
  const projectMap = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects])

  // LOKALES Heute (UTC-Datum gruppierte nach Mitternacht falsch) + Sonntag
  // DIESER Woche (vorher Off-by-one auf den Folge-Montag).
  const todayISO = localTodayISO()
  const weekEnd = new Date(`${todayISO}T00:00:00Z`)
  weekEnd.setUTCDate(weekEnd.getUTCDate() + (6 - ((weekEnd.getUTCDay() + 6) % 7)))
  const weekEndISO = weekEnd.toISOString().slice(0, 10)

  const grouped = useMemo(() => {
    const filtered = tasks.filter((t) => {
      if (!me) return false
      if (filter === 'assigned') return t.assigneeId === me.id
      if (filter === 'created') return t.createdBy === me.id
      return true
    })
    const map = new Map<string, Task[]>()
    for (const t of filtered) {
      const g = dueGroup(t, todayISO, weekEndISO)
      if (g === 'Erledigt' && !showDone) continue
      const list = map.get(g) ?? []
      list.push(t)
      map.set(g, list)
    }
    for (const list of map.values()) {
      list.sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'))
    }
    return map
  }, [tasks, filter, me, todayISO, weekEndISO, showDone])

  if (meLoading) return null
  if (!me) return <Navigate to="/login" />

  return (
    <div className="space-y-4">
      {/* Standard-Kopf statt Eigenbau (Ticket „obere Leiste einheitlich"):
          das frühere max-sm:hidden war mit den raw-Screens der Tailwind-Config
          ein No-op — Phones zeigten den Titel doppelt (h1 + Shell-Leiste).
          keep-row: Filter/Erledigte/Neu existieren mobil nur hier. */}
      <PageHeader
        title="Aufgaben"
        phoneMode="keep-row"
        actions={
          <>
            {/* w-auto: das Select ist per Default w-full und riss die
                Kopfzeile sonst in eine zweite Zeile (Ticket: einreihig). */}
            <Select
              className="w-auto"
              value={filter}
              onChange={(e) => setFilter(e.target.value as FilterMode)}
            >
              <option value="assigned">Mir zugewiesen</option>
              <option value="created">Von mir erstellt</option>
              <option value="all">Alle sichtbaren</option>
            </Select>
            <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={showDone}
                onChange={(e) => setShowDone(e.target.checked)}
                className="h-4 w-4 accent-accent"
              />
              Erledigte
            </label>
            <Button onClick={() => setShowCreate((v) => !v)}>
              <Plus className="h-4 w-4" />
              Neue Aufgabe
            </Button>
          </>
        }
      />

      {showCreate && (
        <CreateTaskForm
          users={manageableUsers}
          projects={projects}
          myId={me.id}
          onDone={() => {
            setShowCreate(false)
            invalidate()
          }}
        />
      )}

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        </div>
      ) : (
        GROUP_ORDER.filter((g) => grouped.has(g)).map((g) => (
          <section key={g} className="space-y-1.5">
            <h2
              className={cn(
                'text-xs font-medium uppercase tracking-wider',
                g === 'Überfällig' ? 'text-danger' : 'text-text-tertiary',
              )}
            >
              {g} ({grouped.get(g)?.length})
            </h2>
            <div className="divide-y divide-border-subtle rounded-xl border border-border-subtle bg-bg-elevated/40">
              {grouped.get(g)?.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  myId={me.id}
                  project={t.projectId ? (projectMap.get(t.projectId) ?? null) : null}
                  canDelete={t.createdBy === me.id || me.role === 'admin'}
                  onToggle={() => toggle.mutate(t)}
                  onDelete={() => remove.mutate(t.id)}
                />
              ))}
            </div>
          </section>
        ))
      )}
      {!isLoading && [...grouped.values()].every((l) => l.length === 0) && (
        <p className="py-10 text-center text-sm text-text-tertiary">
          Keine Aufgaben in dieser Ansicht.
        </p>
      )}
    </div>
  )
}

function TaskRow({
  task,
  myId,
  project,
  canDelete,
  onToggle,
  onDelete,
}: {
  task: Task
  myId: string
  project: { name: string; color: string | null } | null
  canDelete: boolean
  onToggle: () => void
  onDelete: () => void
}) {
  const done = task.status === 'done'
  const StatusIcon = done ? CheckCircle2 : task.status === 'in_progress' ? CircleDot : Circle
  return (
    <div className="group flex items-center gap-3 px-3 py-2.5">
      <button
        type="button"
        onClick={onToggle}
        aria-label={done ? 'Wieder öffnen' : 'Als erledigt markieren'}
        className={cn(
          'shrink-0 rounded-full p-1 touch:p-2',
          done ? 'text-success' : 'text-text-tertiary hover:text-accent',
        )}
      >
        <StatusIcon className="h-5 w-5" />
      </button>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'truncate text-sm',
            done ? 'text-text-tertiary line-through' : 'text-text-primary',
          )}
        >
          {task.title}
        </p>
        <p className="flex flex-wrap items-center gap-x-2 text-2xs text-text-tertiary">
          {task.dueDate && (
            <span className="inline-flex items-center gap-1 font-mono tabular-nums">
              <CalendarDays className="h-3 w-3" />
              {task.dueDate}
            </span>
          )}
          <span className={PRIORITY_COLOR[task.priority]}>{PRIORITY_LABEL[task.priority]}</span>
          {project && (
            <span className="inline-flex items-center gap-1 min-w-0">
              <span
                aria-hidden
                className="inline-block h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: project.color ?? '#6b6259' }}
              />
              <span className="truncate">{project.name}</span>
            </span>
          )}
          {task.assigneeId !== myId && task.assigneeName && (
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" />
              {task.assigneeName}
            </span>
          )}
          {task.createdBy !== myId && task.creatorName && <span>von {task.creatorName}</span>}
        </p>
      </div>
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          aria-label="Aufgabe löschen"
          className="shrink-0 rounded-full p-1 text-text-tertiary opacity-0 transition-opacity hover:text-danger group-hover:opacity-100 touch:opacity-100 touch:p-2"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

function CreateTaskForm({
  users,
  projects,
  myId,
  onDone,
}: {
  users: { id: string; name: string }[]
  projects: { id: string; name: string }[]
  myId: string
  onDone: () => void
}) {
  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [priority, setPriority] = useState('normal')
  const [assigneeId, setAssigneeId] = useState(myId)
  const [projectId, setProjectId] = useState('')

  const create = useMutation({
    mutationFn: () =>
      api.createTask({
        title,
        dueDate: dueDate || null,
        priority,
        assigneeId,
        projectId: projectId || null,
      }),
    onSuccess: () => {
      notify.success('Aufgabe angelegt')
      onDone()
    },
    onError: (e: Error) => notify.error(e.message),
  })

  return (
    <form
      className="grid gap-3 rounded-xl border border-border-subtle bg-bg-elevated/40 p-4 sm:grid-cols-[1fr_auto_auto_auto_auto_auto]"
      onSubmit={(e) => {
        e.preventDefault()
        if (title.trim()) create.mutate()
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="task-title">Titel</Label>
        <Input
          id="task-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Was ist zu tun?"
          maxLength={200}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="task-due">Fällig</Label>
        <Input
          id="task-due"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="task-prio">Priorität</Label>
        <Select id="task-prio" value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="low">Niedrig</option>
          <option value="normal">Normal</option>
          <option value="high">Hoch</option>
          <option value="critical">Kritisch</option>
        </Select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="task-assignee">Zugewiesen an</Label>
        <Select
          id="task-assignee"
          value={assigneeId}
          onChange={(e) => setAssigneeId(e.target.value)}
        >
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.id === myId ? `${u.name} (ich)` : u.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="task-project">Projekt</Label>
        <Select id="task-project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">— Kein Projekt —</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="flex items-end">
        <Button type="submit" disabled={create.isPending || !title.trim()}>
          Anlegen
        </Button>
      </div>
    </form>
  )
}
